import {
  correspondenceReadCorrespondenceThreadOptions,
  useHeyApi,
} from "@platform/api";
import { useQuery } from "@tanstack/react-query";

import type {
  CorrespondenceMessage,
  CorrespondenceThreadEvent,
} from "@platform/api";

export interface CorrespondenceThreadRead {
  messages: CorrespondenceMessage[];
  events: CorrespondenceThreadEvent[];
  /**
   * Whether this analyst may write to the thread.
   *
   * Answered by the BFF rather than guessed here, and it decides whether the
   * reply controls are shown at all rather than shown and then refused.
   */
  writable: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * One thread's messages and timeline.
 *
 * Fetched only once a row is open: a listing carries no bodies, and reading
 * every thread up front would be one request per row for conversations
 * nobody has asked to see.
 */
export const useCorrespondenceThread = (
  threadId: string | undefined,
): CorrespondenceThreadRead => {
  const { centroPortalClient } = useHeyApi();

  const { data, isLoading, isError, refetch } = useQuery({
    ...correspondenceReadCorrespondenceThreadOptions({
      path: { threadId: threadId ?? "" },
      client: centroPortalClient,
    }),
    enabled: !!threadId,
  });

  return {
    messages: data?.messages ?? [],
    events: data?.events ?? [],
    writable: data?.writable ?? false,
    // A row opened before its id has resolved is waiting, not idle: the query
    // is disabled, so react-query reports neither loading nor success.
    isLoading: isLoading || !threadId,
    isError,
    refetch: () => void refetch(),
  };
};
